import { useCallback, useMemo } from 'react'
import { planProjectDelete } from './spaceSidebarUtils'
import type { ProjectDeletePlan } from './spaceSidebarUtils'
import {
  usePageMutations,
  usePages,
  useProjectMutations,
  useProjects,
  useStatuses,
  useTaskMutations,
  useTasks,
} from '@/lib/db/rxdb/hooks'
import { isClosedProgressType } from '@/routes/features/tasks/constants'

/**
 * Deleting a project or space soft-deletes its closed tasks and pages, then a
 * space's projects, then the project itself, each through its own sync path so
 * the server-side cascades run. The project goes last so a failure part-way
 * leaves it in place to retry. Tasks load only while `enabled` (the confirm
 * dialog is open), so sidebar rows don't each walk every task.
 */
export function useDeleteProject(
  projectId: string,
  enabled: boolean,
): ProjectDeletePlan & { isLoading: boolean; deleteProjectTree: () => Promise<void> } {
  const { projects } = useProjects()
  const { pages } = usePages()
  const { tasks, isLoading } = useTasks({ isDeleted: false }, { enabled })
  // A task in a deleted Done status is still closed; the server counts it so too.
  const { statuses } = useStatuses()
  const { statuses: deletedStatuses } = useStatuses({ isDeleted: true })
  const { deleteProject } = useProjectMutations()
  const { deletePage } = usePageMutations()
  const { deleteTask } = useTaskMutations()

  const plan = useMemo(() => {
    const closed = new Set(
      [...statuses, ...deletedStatuses].filter((s) => isClosedProgressType(s.progressType)).map((s) => s.id),
    )
    return planProjectDelete(projectId, projects, pages, tasks, closed)
  }, [projectId, projects, pages, tasks, statuses, deletedStatuses])

  const deleteProjectTree = useCallback(async () => {
    if (plan.isLastSpace || plan.openTaskCount > 0) throw new Error('Project cannot be deleted')
    for (const id of plan.taskIds) await deleteTask(id)
    for (const id of plan.pageIds) await deletePage(id)
    for (const id of plan.projectIds) await deleteProject(id)
    await deleteProject(projectId)
  }, [plan, projectId, deleteTask, deletePage, deleteProject])

  return { ...plan, isLoading: enabled && isLoading, deleteProjectTree }
}
